import { FC } from 'react';

import { RequiredRule } from 'devextreme-react/form';
import { DropDownOptions } from 'devextreme-react/select-box';

import { AppGroup, AppRow } from 'components/appComponents';
import AppFiledBox from 'components/appComponents/app-field-box';
import { DateBox, NumberBox, SelectBox, TagBox, Validator } from 'devextreme-react';
import DataSource from 'devextreme/data/data_source';

import { ContractorPumpPriceCreate } from '../../ContractorPumpPrice.entity';

type Props = {
  form: ContractorPumpPriceCreate;
  onChange: (
    name: keyof ContractorPumpPriceCreate,
    value: ContractorPumpPriceCreate[keyof ContractorPumpPriceCreate],
  ) => void;
  factories: DataSource;
  storeObject: DataSource;
  type: 'EDIT' | 'CREATE';
};

export const ContractorPumpPriceForm: FC<Props> = ({ form, onChange, factories, storeObject, type }) => {
  return (
    <AppRow className="mt-2">
      <AppGroup col={6}>
        <AppFiledBox label="Площадка" labelTop>
          {type === 'CREATE' ? (
            <TagBox
              stylingMode="underlined"
              dataSource={factories}
              displayExpr="name"
              valueExpr="id"
              showSelectionControls={true}
              onValueChanged={(e) => {
                onChange('factoryIds', e.value);
              }}
            >
              <DropDownOptions title="Выберите площадку" closeOnOutsideClick={true} showTitle={false} />
              <Validator>
                <RequiredRule message="Выберите площадку" />
              </Validator>
            </TagBox>
          ) : (
            <SelectBox
              stylingMode="underlined"
              dataSource={factories}
              displayExpr="name"
              valueExpr="id"
              value={form.factoryId}
              onItemClick={(e) => {
                onChange('factoryId', e.itemData.id);
              }}
            >
              <DropDownOptions title="Выберите площадку" closeOnOutsideClick={true} showTitle={false} />
              <Validator>
                <RequiredRule message="Выберите площадку" />
              </Validator>
            </SelectBox>
          )}
        </AppFiledBox>

        <AppFiledBox label="Объект" labelTop>
          <SelectBox
            stylingMode="underlined"
            dataSource={storeObject}
            displayExpr="address"
            valueExpr="id"
            value={form.objectId}
            searchEnabled
            onItemClick={(e) => {
              onChange('objectId', e.itemData.id);
            }}
          >
            <DropDownOptions title="Выберите объект" closeOnOutsideClick={true} showTitle={false} />
          </SelectBox>
        </AppFiledBox>
        <AppFiledBox label="Дата начала" labelTop>
          <DateBox
            stylingMode="underlined"
            type="date"
            onValueChange={(e) => {
              onChange('dateBegin', e as string);
            }}
            useMaskBehavior
            dateSerializationFormat="yyyy-MM-ddTHH:mm:ssx"
            value={form.dateBegin}
          >
            <Validator>
              <RequiredRule message="Заполните поле дата начала" />
            </Validator>
          </DateBox>
        </AppFiledBox>

        <AppFiledBox label="Минимальный объем" labelTop>
          <NumberBox
            stylingMode="underlined"
            value={form.minQuantity || undefined}
            onValueChange={(e) => {
              onChange('minQuantity', e);
            }}
          >
            <Validator>
              <RequiredRule message="Заполните поле Минимальный объем" />
            </Validator>
          </NumberBox>
        </AppFiledBox>

        <AppFiledBox label="Минимальная цена" labelTop>
          <NumberBox
            stylingMode="underlined"
            value={form.pumpPriceMinimal || undefined}
            onValueChange={(e) => {
              onChange('pumpPriceMinimal', e);
            }}
          >
            <Validator>
              <RequiredRule message="Заполните поле Минимальная цена" />
            </Validator>
          </NumberBox>
        </AppFiledBox>

        <AppFiledBox label="Цена за куб" labelTop>
          <NumberBox
            stylingMode="underlined"
            value={form.price || undefined}
            onValueChange={(e) => {
              onChange('price', e);
            }}
          >
            <Validator>
              <RequiredRule message="Заполните поле цена за куб" />
            </Validator>
          </NumberBox>
        </AppFiledBox>

        <AppFiledBox label="Длина стрелы" labelTop>
          <NumberBox
            stylingMode="underlined"
            value={form.pumpLen || undefined}
            onValueChange={(e) => {
              onChange('pumpLen', e);
            }}
          >
            <Validator>
              <RequiredRule message="Заполните поле длина стрелы" />
            </Validator>
          </NumberBox>
        </AppFiledBox>

        <AppFiledBox label="Подача" labelTop>
          <NumberBox
            stylingMode="underlined"
            value={form.pumpInnings ?? undefined}
            onValueChange={(e) => {
              onChange('pumpInnings', e || 0);
            }}
          >
            <Validator>
              <RequiredRule message="Заполните поле Подача" />
            </Validator>
          </NumberBox>
        </AppFiledBox>
      </AppGroup>
      <AppGroup col={6}>
        <AppFiledBox label="Гаситель" labelTop>
          <NumberBox
            stylingMode="underlined"
            value={form.pumpQuencher ?? undefined}
            onValueChange={(e) => {
              onChange('pumpQuencher', e || 0);
            }}
          >
            <Validator>
              <RequiredRule message="Заполните поле Гаситель" />
            </Validator>
          </NumberBox>
        </AppFiledBox>

        <AppFiledBox label="Простой" labelTop>
          <NumberBox
            stylingMode="underlined"
            value={form.downtime ?? undefined}
            onValueChange={(e) => {
              onChange('downtime', e || 0);
            }}
          >
            <Validator>
              <RequiredRule message="Заполните поле Простой" />
            </Validator>
          </NumberBox>
        </AppFiledBox>

        <AppFiledBox label="Переезд" labelTop>
          <NumberBox
            stylingMode="underlined"
            value={form.pumpMoving ?? undefined}
            format="fixedPoint"
            onValueChange={(e) => {
              onChange('pumpMoving', e || 0);
            }}
          >
            <Validator>
              <RequiredRule message="Заполните поле Переезд" />
            </Validator>
          </NumberBox>
        </AppFiledBox>

        <AppFiledBox label="Трубы" labelTop>
          <NumberBox
            stylingMode="underlined"
            value={form.pumpPipes ?? undefined}
            format="fixedPoint"
            onValueChange={(e) => {
              onChange('pumpPipes', e || 0);
            }}
          >
            <Validator>
              <RequiredRule message="Заполните поле Трубы" />
            </Validator>
          </NumberBox>
        </AppFiledBox>
      </AppGroup>
    </AppRow>
  );
};
